import React, { useState, useEffect } from 'react';
import { Box, Button, IconButton, Typography, Avatar, Popover, MenuItem, ListItemIcon, ListItemText, Divider, useMediaQuery, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { HEADER_HEIGHT } from '../../constants';
import LoginDialog from '../Auth/LoginDialog';
import LogoutDialog from '../Auth/LogoutDialog';
import DesktopProfileMenu from '../Auth/DesktopProfileMenu';
import ProfileManageModal from '../Auth/ProfileManageModal';
import { EmailVerificationStatus } from '../Auth';
import { LanguageSelector } from '../common';
import MobileHeaderTop from './MobileHeaderTop';

const LandingHeader = ({ setDrawerOpen, className }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { 
    isLoggedIn, 
    user,
    userEmail,
    userPhoto,
    isSignup,
    setIsSignup,
    email,
    setEmail,
    password,
    setPassword,
    handleEmailAuth,
    handleGoogleAuth,
    handleKakaoAuth,
    handleLogout, 
    loginDialogOpen, 
    setLoginDialogOpen,
    logoutDialogOpen,
    setLogoutDialogOpen,
    // authLoading 제거 - LandingLayout에서 처리
    isResending,
    resendEmailVerification
  } = useAuth();

  // 프로필 팝업 메뉴 상태
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const profilePopoverOpen = Boolean(profileAnchorEl);
  
  // 프로필 관리 모달 상태
  const [profileManageModalOpen, setProfileManageModalOpen] = useState(false);

  // 프로필 메뉴 핸들러들
  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const handleProfileLogout = () => {
    setProfileAnchorEl(null);
    setLogoutDialogOpen(true);
  };

  const handleProfileManage = () => {
    setProfileAnchorEl(null);
    setProfileManageModalOpen(true);
  };

  const handleProfileManageClose = () => {
    setProfileManageModalOpen(false);
  };

  const handleProfileSave = async (profileData) => {
    // TODO: 프로필 저장 로직 구현
    console.log('프로필 저장:', profileData);
  };

  const handlePasswordChange = async (passwordData) => {
    // TODO: 실제 비밀번호 변경 API 호출 구현
    console.log('비밀번호 변경:', passwordData);
    // 성공 시 메시지 표시 등의 로직 추가
  };

  const handleAccountDelete = () => {
    // TODO: 회원탈퇴 로직 구현
    console.log('회원탈퇴');
  };

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  const handleDrawerToggle = () => {
    if (setDrawerOpen) {
      setDrawerOpen(true);
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  // 데스크톱에서는 기존 레이아웃 유지
  if (!isMobile) {
    return (
      <Box
        component="header"
        className={className}
        sx={{
          height: HEADER_HEIGHT,
          bgcolor: 'background.paper',
          borderBottom: 'none',
          display: 'flex',
          alignItems: 'center',
          px: 3,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          {isLoggedIn && setDrawerOpen && (
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{ 
                mr: { xs: 0, md: 3 }
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            onClick={() => navigate('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
          >
            <img src="/logo.png" alt="TubeDigest 로고" style={{ width: 26, height: 26, objectFit: 'contain', borderRadius: 6 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 22, color: '#222', letterSpacing: -0.8, fontFamily: 'Roboto, sans-serif' }}>
              TubeDigest
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LanguageSelector />
          
          {isLoggedIn ? (
            <Avatar
              src={userPhoto}
              onClick={handleProfileClick}
              sx={{
                width: 36,
                height: 36,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8
                }
              }}
            >
              {!userPhoto && <PersonIcon />}
            </Avatar>
          ) : (
            <>
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <IconButton
                  onClick={handleLoginClick}
                  sx={{
                    color: '#1976d2',
                    '&:hover': {
                      bgcolor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  <LoginIcon />
                </IconButton>
              </Box>
              <Button
                variant="contained"
                onClick={handleLoginClick}
                sx={{
                  display: { xs: 'none', md: 'block' },
                  minWidth: 100,
                  bgcolor: '#1976d2',
                  '&:hover': {
                    bgcolor: '#1565c0'
                  }
                }}
              >
                {t('common.login')}
              </Button>
            </>
          )}
        </Box>

        {/* 프로필 팝업 메뉴 */}
        <DesktopProfileMenu
          open={profilePopoverOpen}
          anchorEl={profileAnchorEl}
          onClose={handleProfileClose}
          onLogout={handleProfileLogout}
          onProfileManage={handleProfileManage}
          userEmail={userEmail}
          t={t}
        />

        <LoginDialog
          open={loginDialogOpen}
          onClose={() => setLoginDialogOpen(false)}
          // 모달 초기화는 LoginDialog 내부에서 처리됨
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          isSignup={isSignup}
          setIsSignup={setIsSignup}
          handleEmailAuth={handleEmailAuth}
          handleGoogleAuth={handleGoogleAuth}
          handleKakaoAuth={handleKakaoAuth}
        />

        <LogoutDialog
          open={logoutDialogOpen}
          onClose={() => setLogoutDialogOpen(false)}
          onConfirm={handleLogout}
        />

        {/* 프로필 관리 모달 */}
        <ProfileManageModal
          open={profileManageModalOpen}
          onClose={handleProfileManageClose}
          user={user}
          userEmail={userEmail}
          onSave={handleProfileSave}
          onPasswordChange={handlePasswordChange}
          onAccountDelete={handleAccountDelete}
        />
      </Box>
    );
  }

  // 모바일에서는 MobileHeaderTop 사용 (analyze페이지와 동일)
  return (
    <Box
      component="header"
      className={className}
      sx={{
        bgcolor: '#fff',
        borderBottom: '1px solid #eee',
        width: '100%',
        zIndex: 1200,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        paddingTop: 'env(safe-area-inset-top, 0px)'
      }}
    >
      <MobileHeaderTop
        onDrawerToggle={handleDrawerToggle}
        onLogoClick={handleLogoClick}
        onProfileMenuOpen={handleProfileClick}
        onLogin={handleLoginClick}
        user={{ displayName: userEmail, email: userEmail, photo: userPhoto }}
        t={t}
        isLoggedIn={isLoggedIn}
        hideLoginButton={false}
        showMenuButton={true}  // 랜딩페이지에서는 로그인 상태일 때만 햄버거 버튼 표시 (MobileHeaderTop 내부에서 조건 체크)
      />

      {/* 프로필 팝업 메뉴 */}
      <DesktopProfileMenu
        open={profilePopoverOpen}
        anchorEl={profileAnchorEl}
        onClose={handleProfileClose}
        onLogout={handleProfileLogout}
        onProfileManage={handleProfileManage}
        userEmail={userEmail}
        t={t}
      />

      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        isSignup={isSignup}
        setIsSignup={setIsSignup}
        handleEmailAuth={handleEmailAuth}
        handleGoogleAuth={handleGoogleAuth}
        handleKakaoAuth={handleKakaoAuth}
      />

      <LogoutDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleLogout}
      />

      {/* 프로필 관리 모달 */}
      <ProfileManageModal
        open={profileManageModalOpen}
        onClose={handleProfileManageClose}
        user={user}
        userEmail={userEmail}
        onSave={handleProfileSave}
        onPasswordChange={handlePasswordChange}
        onAccountDelete={handleAccountDelete}
      />
    </Box>
  );
};

export default LandingHeader; 